import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  APPROVAL_REJECTED,
  EXPIRED,
  LOI_STATUS_CONFIG,
  SEND_FAILED,
  TERMINAL_STATUSES,
  withSignatureProgress,
} from 'v2/helpers/status/loi';
import { rejectedRed, mutedGray } from 'v2/constants/colors';

/**
 * The LOI status reads as plain text, exactly like the order row's status, so
 * the two sit in one table without one shouting louder than the other.
 */
const StatusText = styled('span')({
  whiteSpace: 'nowrap',
});

const SupportingText = styled(Typography)({
  fontSize: 11,
  lineHeight: 1.4,
  color: mutedGray,
  marginTop: 3,
});

const formatExpiry = (value) => {
  const parsed = moment(value, 'YYYY-MM-DD HH:mm:ss');
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : null;
};

/**
 * The supporting line never becomes part of the headline. It sits beneath the
 * badge so the headline stays exactly one lifecycle status.
 */
const supportingFor = (row, status, t) => {
  const { expiry_date: expiryDate } = row;

  if (status === SEND_FAILED) {
    return t('loi-send-failed-hint');
  }

  const expiry = expiryDate ? formatExpiry(expiryDate) : null;
  if (!expiry) return null;

  if (status === EXPIRED) {
    return t('loi-expired-on', { date: expiry });
  }
  if (!TERMINAL_STATUSES.includes(status)) {
    return t('loi-expires-on', { date: expiry });
  }

  return null;
};

const LoiStatusCell = ({ row, status, rawStatusLabel, onRejectionClick }) => {
  const { t } = useTranslation();
  const config = LOI_STATUS_CONFIG[status];

  if (!config) {
    if (!rawStatusLabel) return null;
    return (
      <Box data-testid={`loi-status-${row.loi_id}`}>
        <StatusText data-testid="loi-status-label-unmapped">
          {withSignatureProgress(rawStatusLabel, row.signatory)}
        </StatusText>
      </Box>
    );
  }

  const supporting = supportingFor(row, status, t);
  const showRejectionInfo = status === APPROVAL_REJECTED;

  return (
    <Box data-testid={`loi-status-${row.loi_id}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <StatusText data-testid={`loi-status-label-${status}`}>
          {t(config.labelKey)}
        </StatusText>

        {status === SEND_FAILED && (
          <ErrorOutlineIcon sx={{ fontSize: 16, color: rejectedRed }} />
        )}

        {showRejectionInfo && onRejectionClick && (
          <IconButton
            data-testid={`loi-rejection-info-${row.loi_id}`}
            aria-label={t('view-rejection-details')}
            size="small"
            sx={{ padding: '2px', color: rejectedRed }}
            onClick={(event) => onRejectionClick(event, row)}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {supporting && (
        <SupportingText data-testid={`loi-status-supporting-${row.loi_id}`}>
          {supporting}
        </SupportingText>
      )}
    </Box>
  );
};

LoiStatusCell.propTypes = {
  row: PropTypes.shape({
    loi_id: PropTypes.number,
    assigned_approvers: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
    ]),
    expiry_date: PropTypes.string,
    signatory: PropTypes.shape({
      total: PropTypes.number,
      signers: PropTypes.number,
    }),
  }).isRequired,
  status: PropTypes.string,
  rawStatusLabel: PropTypes.string,
  onRejectionClick: PropTypes.func,
};

export default LoiStatusCell;
